import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '../api/client'
import { createRfqBatch } from '../api/rfqs'
import { useAuth } from '../auth/AuthProvider'
import { startPlanUiCopy } from '../billing/startPlanUiCopy'
import { useBuyerEntitlements } from '../hooks/useBuyerEntitlements'
import { usePlanGate } from '../hooks/usePlanGate'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { useCart } from '../cart/CartProvider'
import { groupCartLines } from '../cart/groupCartLines'
import { defaultSupplierRfqTitle } from '../cart/rfqTitles'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { RfqDraftTreeTable } from './RfqDraftTreeTable'

export const RfqCreateScreen = () => {
  const { session } = useAuth()
  const entitlements = useBuyerEntitlements()
  const { isFeatureEnabled, requestFeature, paywallModal } = usePlanGate()
  const { activeVenueId } = useActiveVenue()
  const navigate = useNavigate()
  const { lines, lineCount, updateQuantity, updateNote, removeLine, clearCart } = useCart()

  useEffect(() => {
    if (!isFeatureEnabled('platform_rfq')) {
      requestFeature('platform_rfq')
    }
  }, [isFeatureEnabled, requestFeature])

  const grouped = useMemo(() => groupCartLines(lines), [lines])
  const supplierGroups = grouped.inProvisium
  const externalLineCount = grouped.external.reduce((sum, group) => sum + group.lineCount, 0)
  const [description, setDescription] = useState('')
  const [desiredDeliveryDate, setDesiredDeliveryDate] = useState('')
  const [desiredDeliveryTimeUntil, setDesiredDeliveryTimeUntil] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')
  const [supplierTitles, setSupplierTitles] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const supplierCount = supplierGroups.length
  const defaultTitle = defaultSupplierRfqTitle()

  const resolveSupplierTitle = (supplierOrganizationId: number) => {
    const title = supplierTitles[supplierOrganizationId]?.trim()
    return title || defaultTitle
  }

  if (!isFeatureEnabled('platform_rfq')) {
    return (
      <section className="market-page market-page--wide">
        <MarketPageHeader
          title="Сформировать заявки"
          description="Заявки будут автоматически разбиты по поставщикам."
        />
        <MarketEmptyState
          title="Заявки недоступны"
          description={startPlanUiCopy.rfqCreateBlockedDescription}
          actions={[
            { label: 'Мои прайсы', to: '/my-prices' },
            { label: 'Корзина', to: '/cart', variant: 'ghost' },
          ]}
        />
        {paywallModal}
      </section>
    )
  }

  if (lineCount === 0 || supplierGroups.length === 0) {
    return (
      <section className="market-page market-page--wide">
        <MarketPageHeader
          title="Сформировать заявки"
          description="Заявки будут автоматически разбиты по поставщикам."
        />
        <MarketEmptyState
          title={externalLineCount > 0 ? 'Нет позиций для заявки' : 'Нет позиций для оформления'}
          description={
            externalLineCount > 0
              ? 'В корзине есть позиции «вне системы» — для них используйте бланк заказа в корзине. Добавьте товары от поставщиков на платформе для отправки заявки.'
              : entitlements.isStartPlan
                ? startPlanUiCopy.rfqCreateBlockedDescription
                : 'Добавьте товары в корзину из каталога, затем вернитесь к оформлению.'
          }
          actions={
            entitlements.isStartPlan
              ? [
                  { label: 'Мои прайсы', to: '/my-prices' },
                  { label: 'Корзина', to: '/cart', variant: 'ghost' },
                ]
              : [
                  { label: 'Перейти в каталог', onClick: () => navigate('/catalog') },
                  { label: 'Корзина', onClick: () => navigate('/cart'), variant: 'ghost' },
                ]
          }
        />
      </section>
    )
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!session) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const created = await createRfqBatch({
        organizationId: session.organizationId,
        venueId: activeVenueId,
        description: description.trim() || null,
        desiredDeliveryDate: desiredDeliveryDate.trim() || null,
        desiredDeliveryTimeUntil: desiredDeliveryTimeUntil.trim() || null,
        deliveryAddress: deliveryAddress.trim() || null,
        items: supplierGroups
          .filter((supplier) => supplier.supplierOrganizationId != null)
          .map((supplier) => ({
          supplierOrganizationId: supplier.supplierOrganizationId as number,
          title: resolveSupplierTitle(supplier.supplierOrganizationId as number),
          lines: supplier.categories.flatMap((category) =>
            category.lines.map((line) => ({
              productTypeId: line.productTypeId,
              quantity: line.quantity,
              // Send the raw catalog unit (CartLine.unit), not the display
              // label - the backend compares it verbatim against the
              // supplier catalog item's own unit string.
              unit: line.unit,
              note: line.note.trim() || line.itemName || null,
            })),
          ),
        })),
      })
      const createdIds = created.map((rfq) => rfq.id)

      clearCart()
      const query = createdIds.length === 1 ? `?rfqId=${createdIds[0]}` : ''
      const flash =
        created.length === 1
          ? 'Заявка отправлена поставщику'
          : `Отправлено заявок: ${created.length}`
      navigate(`/orders${query}`, { state: { flash } })
    } catch (submitError) {
      if (submitError instanceof ApiError) {
        setError(submitError.message || 'Не удалось отправить заявки')
      } else {
        setError('Не удалось отправить заявки')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="market-page market-page--wide market-page--with-action-bar">
      <MarketPageHeader
        title="Сформировать заявки"
        description={`${lineCount} поз. · ${supplierCount} поставщ.`}
        actions={
          <button type="button" className="echat-btn echat-btn--ghost" onClick={() => navigate('/cart')}>
            ← Корзина
          </button>
        }
      />

      <p className="market-hint">
        Заявки будут автоматически разбиты по поставщикам — у каждого поставщика свой номер и своя заявка.
      </p>

      <form className="market-rfq-draft" onSubmit={(event) => void handleSubmit(event)}>
        <div className="market-table-wrap market-table-wrap--cards market-rfq-draft__delivery">
          <table className="market-table market-table--cards">
            <thead>
              <tr>
                <th>Дата доставки</th>
                <th>До (время)</th>
                <th>Адрес</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    className="echat-field__input"
                    type="date"
                    value={desiredDeliveryDate}
                    onChange={(event) => setDesiredDeliveryDate(event.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="echat-field__input"
                    type="time"
                    value={desiredDeliveryTimeUntil}
                    onChange={(event) => setDesiredDeliveryTimeUntil(event.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="echat-field__input"
                    value={deliveryAddress}
                    placeholder="Адрес доставки"
                    onChange={(event) => setDeliveryAddress(event.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="echat-field__input"
                    value={description}
                    placeholder="Общий комментарий"
                    onChange={(event) => setDescription(event.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <RfqDraftTreeTable
          lines={lines}
          supplierTitles={supplierTitles}
          onSupplierTitleChange={(supplierOrganizationId, title) =>
            setSupplierTitles((current) => ({ ...current, [supplierOrganizationId]: title }))
          }
          onQuantityChange={updateQuantity}
          onNoteChange={updateNote}
          onRemoveLine={removeLine}
        />

        {error ? (
          <p className="echat-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="market-inline-actions market-rfq-draft__desktop-actions">
          <button type="button" className="echat-btn echat-btn--ghost" onClick={() => navigate('/catalog')}>
            Добавить из каталога
          </button>
          <button type="submit" className="echat-btn echat-btn--primary" disabled={loading}>
            {loading ? 'Отправка…' : `Отправить ${supplierCount} заявок`}
          </button>
        </div>
      </form>

      <div className="market-action-bar" role="region" aria-label="Отправка заявок">
        <span className="market-action-bar__summary">
          {lineCount} поз. · {supplierCount} поставщ.
        </span>
        <div className="market-action-bar__actions">
          <button type="button" className="echat-btn echat-btn--ghost" onClick={() => navigate('/catalog')}>
            В каталог
          </button>
          <button
            type="button"
            className="echat-btn echat-btn--primary"
            disabled={loading}
            onClick={() => {
              const form = document.querySelector<HTMLFormElement>('.market-rfq-draft')
              form?.requestSubmit()
            }}
          >
            {loading ? 'Отправка…' : `Отправить ${supplierCount} заявок`}
          </button>
        </div>
      </div>
    </section>
  )
}
